import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, FileText, Trash2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import type { Database } from "@/lib/supabase";

type AccountAssistant = Database['public']['Tables']['account_assistants']['Row']

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface TestLog {
  id: string;
  timestamp: Date;
  assistant_name: string;
  last_message_id_sent: string | null;
  request_url: string;
  request_headers: Record<string, string>;
  request: any;
  response: any;
  http_status: number;
  last_message_id_received: string | null;
}

interface AssistantSimulationChatProps {
  assistant?: AccountAssistant;
}

const AssistantSimulationChat: React.FC<AssistantSimulationChatProps> = ({ assistant }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const [testLogs, setTestLogs] = useState<TestLog[]>([]);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userText = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      const requestTimestamp = new Date();
      const lastMessageIdSent = lastMessageId;

      const payload = {
        last_message_id: lastMessageId,
        text: userText,
        assistant_id: assistant?.id || null
      };

      const requestUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/processamento-ia`;
      const requestHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      };

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(payload)
      });

      const httpStatus = response.status;

      if (!response.ok) {
        const errorText = await response.text();

        const errorLog: TestLog = {
          id: Date.now().toString(),
          timestamp: requestTimestamp,
          assistant_name: assistant?.name || 'Assistente de Teste',
          last_message_id_sent: lastMessageIdSent,
          request_url: requestUrl,
          request_headers: requestHeaders,
          request: payload,
          response: { error: errorText },
          http_status: httpStatus,
          last_message_id_received: null
        };
        setTestLogs(prev => [errorLog, ...prev]);

        throw new Error(`Edge function error: ${response.status}`);
      }

      const result = await response.json();

      const successLog: TestLog = {
        id: Date.now().toString(),
        timestamp: requestTimestamp,
        assistant_name: assistant?.name || 'Assistente de Teste',
        last_message_id_sent: lastMessageIdSent,
        request_url: requestUrl,
        request_headers: requestHeaders,
        request: payload,
        response: result,
        http_status: httpStatus,
        last_message_id_received: result.last_message_id || null
      };
      setTestLogs(prev => [successLog, ...prev]);

      if (result.success && result.last_message_id) {
        setLastMessageId(result.last_message_id);
      }

      if (result.success && result.text) {
        // Split response by triple line breaks (\n\n\n) and create multiple messages
        const textLines = result.text.split('\n\n\n').filter((line: string) => line.trim() !== '');

        const botMessages: Message[] = textLines.map((line: string, index: number) => ({
          id: result.last_message_id ? `${result.last_message_id}_${index}` : `${Date.now()}_${index}`,
          text: line.trim(),
          isUser: false,
          timestamp: new Date()
        }));

        setMessages(prev => [...prev, ...botMessages]);
      } else {
        throw new Error(result.error || 'Nenhuma resposta gerada pela IA');
      }

    } catch (error) {
      console.error('Erro ao processar mensagem:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: '❌ Erro ao processar sua mensagem. Verifique se a chave da OpenAI está configurada.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearLogs = () => {
    if (confirm('Tem certeza que deseja limpar todos os logs de teste?')) {
      setTestLogs([]);
    }
  };

  const handleNewConversation = () => {
    if (confirm('Tem certeza que deseja iniciar uma nova conversa? Isso limpará o chat e os logs.')) {
      setMessages([]);
      setLastMessageId(null);
      setTestLogs([]);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#18181B] border border-[#3F3F464D] rounded-lg">
      <div className="p-4 border-b border-[#3F3F464D] flex items-center justify-between">
        <div>
          <h3 className="text-neutral-50 font-semibold text-sm">Simulação do Assistente</h3>
          <p className="text-neutral-400 text-xs mt-1">Teste como o assistente responderá</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewConversation}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            title="Nova conversa"
          >
            <RefreshCw size={16} className="text-neutral-50" />
            <span className="text-neutral-50 text-xs font-medium">Nova Conversa</span>
          </button>
          <Dialog open={isLogModalOpen} onOpenChange={setIsLogModalOpen}>
            <DialogTrigger asChild>
              <button className="relative flex items-center gap-2 px-3 py-2 bg-neutral-600 hover:bg-neutral-500 rounded-lg transition-colors">
                <FileText size={16} className="text-neutral-50" />
                <span className="text-neutral-50 text-xs font-medium">Logs</span>
                {testLogs.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {testLogs.length}
                  </span>
                )}
              </button>
            </DialogTrigger>
          <DialogContent className="flex !max-w-[900px] rounded-[8px] w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col max-h-[90vh]">
            <div className="p-5">
              <DialogHeader className="flex flex-row items-center justify-between">
                <DialogTitle className="text-[18px] text-neutral-50 font-semibold">
                  Histórico de Testes
                </DialogTitle>
                <button
                  onClick={handleClearLogs}
                  disabled={testLogs.length === 0}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-neutral-600 disabled:cursor-not-allowed rounded-lg transition-colors text-sm text-white"
                >
                  <Trash2 size={14} />
                  Limpar Logs
                </button>
              </DialogHeader>

              {testLogs.length === 0 ? (
                <div className="mt-6 text-center py-12">
                  <FileText size={48} className="mx-auto text-neutral-600 mb-4" />
                  <p className="text-neutral-400">Nenhum teste realizado ainda</p>
                  <p className="text-neutral-500 text-sm mt-2">Os logs aparecerão aqui quando você testar o assistente</p>
                </div>
              ) : (
                <ScrollArea classNames={{ root: "mt-4 max-h-[70vh] overflow-auto" }}>
                  <div className="space-y-3">
                    {testLogs.map((log) => (
                      <div
                        key={log.id}
                        className="border border-[#3F3F464D] rounded-lg p-4 bg-[#27272A] hover:bg-[#2d2d32] transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-neutral-50 font-semibold text-sm">{log.assistant_name}</h4>
                            <p className="text-neutral-400 text-xs mt-1">
                              {log.timestamp.toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </p>
                            <div className="mt-2 space-y-1">
                              <p className="text-neutral-500 text-xs">
                                <span className="font-semibold">Enviado:</span>{' '}
                                {log.last_message_id_sent ? (
                                  <span className="font-mono text-yellow-400">{log.last_message_id_sent.substring(0, 20)}...</span>
                                ) : (
                                  <span className="text-blue-400">Nova conversa</span>
                                )}
                              </p>
                              <p className="text-neutral-500 text-xs">
                                <span className="font-semibold">Recebido:</span>{' '}
                                {log.last_message_id_received ? (
                                  <span className="font-mono text-green-400">{log.last_message_id_received.substring(0, 20)}...</span>
                                ) : (
                                  <span className="text-red-400">Nenhum</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              log.http_status === 200
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}
                          >
                            HTTP {log.http_status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <h5 className="text-neutral-300 text-xs font-semibold mb-2 flex items-center gap-2">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              Request
                            </h5>
                            <pre className="bg-[#18181B] border border-[#3F3F464D] rounded p-3 text-xs text-neutral-300 overflow-x-auto max-h-[200px] overflow-y-auto">
                              {JSON.stringify(log.request, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h5 className="text-neutral-300 text-xs font-semibold mb-2 flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              Response
                            </h5>
                            <pre className="bg-[#18181B] border border-[#3F3F464D] rounded p-3 text-xs text-neutral-300 overflow-x-auto max-h-[200px] overflow-y-auto">
                              {JSON.stringify(log.response, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.isUser
                  ? 'bg-blue-600 text-white'
                  : 'bg-[#27272A] text-neutral-50'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
              <span className="text-xs opacity-70 mt-1 block">
                {message.timestamp.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[#27272A] rounded-lg p-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
              <span className="text-sm text-neutral-400">Processando...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-[#3F3F464D]">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            className="flex-1 bg-[#27272A] border border-[#3F3F464D] rounded-lg px-3 py-2 text-neutral-50 text-sm placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-600 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 transition-colors"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantSimulationChat;
